import { useState, useEffect, Fragment, useContext } from "react";
import { Typography } from "components/Typography";
import { fetchPopularService, IPopularItem } from "services/popular";
import styled from "styled-components";
import { addFavoriteService, deleteFavoriteService } from "services/favorite";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { faStar as EmptyStar } from "@fortawesome/free-regular-svg-icons";
import { getUserId } from "utils/getUserId";
import { Loader } from "components/Loader";
import { IProfileResponse } from "services/profile";
import { CalculatorContext } from "App";

interface IProps {
  profiles: IProfileResponse[];
  profileType: string;
  handleClick: (e: any, data: IPopularItem) => void;
  fetchFavoriteProfiles: any;
  favorites: any[];
  setIsFavorite: (e: boolean) => void;
}

const Root = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.7);
  border-radius: 0;
  background-color: #f7f6f6;
  padding: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: 0;
  cursor: pointer;
  position: relative;
  @media (min-width: 800px) {
    border: 1px solid rgba(0, 0, 0, 0.3);
    background-color: #fffdfd;
    display: flex;
    margin: 2rem 0;
    padding: 1.25rem;
  }
`;
const Wrapper = styled.div``;
const PopularItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0 0;
  line-height: 25px;
  background-color: transparent;
`;
const Pipe = styled.div`
  @media (min-width: 800px) {
    margin: 0 5px;
  }
`;
const Flex = styled.div`
  display: flex;
`;
const FlexHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  @media (min-width: 800px) {
    flex-direction: row;
  }
`;
const StyledTypography = styled(Typography)`
  margin-right: 10px;
  white-space: nowrap;
  text-decoration: underline;
`;

const IconImage = styled.img`
  width: 20px; /* adjust width as needed */
  height: 20px; /* adjust height as needed */
  margin-right: 5px; /* adjust spacing as needed */
`;

export const Popular = ({
  handleClick,
  fetchFavoriteProfiles,
  favorites,
  profiles,
  profileType,
  setIsFavorite,
}: IProps) => {
  const [visibleList, setVisibleList] = useState(false);
  const [populars, setPopulars] = useState<IPopularItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const context = useContext(CalculatorContext);

  const { translations } = context!;

  const userId = getUserId();

  const fetchPopular = async () => {
    try {
      setIsLoading(true);
      const response = await fetchPopularService();

      setPopulars(response.data.items);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRootClick = () => setVisibleList(!visibleList);

  useEffect(() => {
    fetchPopular();
  }, []);

  const addToFavorite = async (event, item) => {
    event.stopPropagation();
    setIsLoading(true);

    const payload = {
      profileType: item.profile.profileType,
      filledFields: item.filledFields,
      popularId: item.id,
    };

    try {
      await addFavoriteService(userId, payload);
      fetchFavoriteProfiles();
      setPopulars((prevPopulars) =>
        prevPopulars.filter((popularItem) => popularItem.id !== item.id)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromFavorite = async (e, item: any) => {
    setIsLoading(true);
    try {
      await deleteFavoriteService(item.id);
      fetchFavoriteProfiles();
      setIsFavorite(false);
    } finally {
      setIsLoading(false);
    }
  };

  const displayedFormatIds = favorites.map((item) =>
    item.filledFields.map((field) => field.value).join("-")
  );

  const uniqueFavorites = favorites.filter(
    (item, index) =>
      displayedFormatIds.indexOf(
        item.filledFields.map((field) => field.value).join("-")
      ) === index
  );

  const activeProfileType = profiles.find(
    (item) => item.profileType === profileType
  )?.profileType;

  const popularFiltered = populars
    .filter((item) => item.profile.profileType === activeProfileType)
    .filter((item) => {
      const formattedFields = item.filledFields
        .map((field) => field.value)
        .join("-");
      return !displayedFormatIds.includes(formattedFields);
    });

  // console.log({ uniqueFavorites });
  // console.log({ test });

  return (
    <>
      <Root onClick={handleRootClick}>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {uniqueFavorites.length === 0 && popularFiltered.length === 0 ? (
              <Typography variant="small">
                {translations["feel-free-to-save-your-favorite-formats"]}
              </Typography>
            ) : (
              <>
                <Wrapper>
                  <FlexHeader>
                    <Typography variant="small" fontWeight={700}>
                      {translations["dont-have-formats"]}
                    </Typography>
                    <StyledTypography variant="small">
                      {translations["see-popular-formats"]}
                    </StyledTypography>
                  </FlexHeader>
                  {visibleList && (
                    <>
                      {uniqueFavorites.map((item, index) => (
                        <PopularItem
                          key={index}
                          onClick={(e) => handleClick(e, item)}
                        >
                          <Flex>
                            <IconImage src={item.profile.iconUrl} alt="Icon" />
                            {item.filledFields.map((field, index) => (
                              <Fragment key={index}>
                                <Flex>
                                  <Typography variant="small">
                                    {field.value}
                                  </Typography>
                                </Flex>
                                {index < item.filledFields.length - 1 && (
                                  <Pipe>x</Pipe>
                                )}
                                {index === item.filledFields.length - 1 && (
                                  <Pipe>mm</Pipe>
                                )}
                              </Fragment>
                            ))}
                          </Flex>
                          <FontAwesomeIcon
                            icon={faStar}
                            className="fa-star"
                            onClick={(e) => removeFromFavorite(e, item)}
                          />
                        </PopularItem>
                      ))}
                    </>
                  )}
                  {visibleList && (
                    <>
                      {popularFiltered.map((item, index) => (
                        <PopularItem
                          key={index}
                          onClick={(e) => handleClick(e, item)}
                        >
                          <Flex>
                            <IconImage src={item.profile.iconUrl} alt="Icon" />
                            {item.filledFields.map((field, index) => (
                              <Fragment key={index}>
                                <Flex>
                                  <Typography variant="small">
                                    {field.value}
                                  </Typography>
                                </Flex>
                                {index < item.filledFields.length - 1 && (
                                  <Pipe>x</Pipe>
                                )}
                                {index === item.filledFields.length - 1 && (
                                  <Pipe>mm</Pipe>
                                )}
                              </Fragment>
                            ))}
                          </Flex>
                          <FontAwesomeIcon
                            icon={EmptyStar}
                            glyphName="regular"
                            onClick={(e) => addToFavorite(e, item)}
                          />
                        </PopularItem>
                      ))}
                    </>
                  )}
                </Wrapper>

                <Typography variant="body">
                  {translations["favorites-elements"]}{" "}
                  <FontAwesomeIcon icon={faStar} />
                </Typography>
              </>
            )}
          </>
        )}
      </Root>
    </>
  );
};
